// Ensure the i18n initializer side-effects run before any hook usage.
import i18n from '@/utils/i18n';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

// A small helper that returns a 'safe' t() which returns the translation only
// when i18next reports it's ready. Until ready it returns a deterministic
// fallback (the provided fallback or the key). This keeps server and initial
// client render identical and avoids hydration mismatch warnings.
export default function useSafeTranslation() {
  const { t, ready, i18n: i18nObj } = useTranslation();
  // consider server rendering as not-ready for deterministic output to avoid
  // hydration mismatches: server may initialize i18n synchronously and report
  // ready=true while the client is not yet ready at hydration time.

  const tSafe = ((key: string, options?: Record<string, unknown>) => {
    // If react-i18next reports ready, use its t() (it will consider react options)
    if (ready) {
      return t(key, options);
    }

    // If on the server, try to use the shared i18n instance which was
    // synchronously initialized with static JSON resources. This ensures the
    // server and the initial client render produce the same text when
    // resources are available.
    try {
      if (i18n && typeof i18n.t === 'function') {
        const translated = i18n.t(key, options);
        // i18next returns the key when no translation exists; only return the
        // translation when it differs from the key (so we stay deterministic).
        if (translated && translated !== String(key)) {
          return translated;
        }
      }
    } catch (e) {
      // fallthrough to deterministic fallback below
    }

    // deterministic fallback (keep stable text across server and initial client)
    if (
      options &&
      typeof options === 'object' &&
      'defaultValue' in options &&
      typeof options.defaultValue === 'string'
    ) {
      return options.defaultValue;
    }
    return String(key);
  }) as unknown as TFunction;

  return { tSafe, t, ready, i18n: i18nObj } as const;
}
